import useSWR from 'swr';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Euro, ArrowRight, Mountain, Heart } from 'lucide-react';
import { Circuit } from '@/types';
import { cn } from '@/utils/cn';
import { useState, useEffect } from 'react';
import { useFavoritesStore } from '@/stores/favoritesStore';

interface TripPreviewCardProps {
    circuit: Circuit;
    onClick: () => void;
    className?: string;
}

const fetcher = (url: string) => fetch(url).then(r => r.json());

/**
 * Animated gradient bar component for loading states
 */
function GradientLoadingBar({ className }: { className?: string }) {
    return (
        <div className={cn("relative w-full overflow-hidden rounded-full", className)}>
            {/* Base bar */}
            <div className="h-full w-full bg-white/10 rounded-full" />
            
            {/* Animated gradient bar */}
            <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                    backgroundSize: '200% 100%',
                }}
                animate={{
                    backgroundPosition: ['-200% 0', '200% 0'],
                    opacity: [0.3, 1, 0.3],
                }}
                transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
            />
        </div>
    );
}

/**
 * Shimmer animation component for missing images
 */
function ShimmerPlaceholder({ className }: { className?: string }) {
    return (
        <div className={cn("relative w-full h-full overflow-hidden", className)}>
            {/* Base background */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-700/60 via-slate-800/60 to-slate-700/60" />
            
            {/* Shimmer effect */}
            <motion.div
                className="absolute inset-0"
                style={{
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 45%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 55%, transparent 100%)',
                    backgroundSize: '200% 100%',
                }}
                animate={{
                    backgroundPosition: ['-200% 0', '200% 0'],
                }}
                transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "linear",
                }}
            />
            
            {/* Additional shimmer layers for depth */}
            <motion.div
                className="absolute inset-0"
                style={{
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.05) 40%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.05) 60%, transparent 100%)',
                    backgroundSize: '300% 100%',
                }}
                animate={{
                    backgroundPosition: ['-300% 0', '300% 0'],
                }}
                transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "linear",
                    delay: 0.5,
                }}
            />
        </div>
    );
}

export function TripPreviewCard({ circuit, onClick, className }: TripPreviewCardProps) {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [textMinDelayElapsed, setTextMinDelayElapsed] = useState(false);
    const { isFavorite, toggleFavorite } = useFavoritesStore();

    // Use Microlink.io public API for client-side OG fetching
    const { data, isLoading, error } = useSWR(
        circuit.url ? `https://api.microlink.io?url=${encodeURIComponent(circuit.url)}&palette=true` : null,
        fetcher
    );

    const ogImage = data?.data?.image?.url;
    const ogDescription = data?.data?.description;

    // Only use ogDescription (resume, image_url, images are no longer sent by worker)
    const displayResume = ogDescription;
    
    // Reset text delay when circuit changes
    useEffect(() => {
        setTextMinDelayElapsed(false);
        // Removed artificial 2-second delay - only showing loading during actual data fetch
        setTextMinDelayElapsed(true);
    }, [circuit.id]);

    // Show loading spinner if: still loading OR minimum delay not elapsed OR no ogDescription yet
    const isTextLoading = !textMinDelayElapsed || (!ogDescription && !error && isLoading && circuit.url);

    // Only use ogImage (image_url and images are no longer sent by worker)
    const displayImage = ogImage;
    
    // Reset image state when circuit or image URL changes
    useEffect(() => {
        setImageLoaded(false);
        setImageError(false);
    }, [circuit.id, displayImage]);

    const isImageLoading = displayImage && !imageLoaded && !imageError; return (
        <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 20, stiffness: 120 }}
            onClick={onClick}
            className={cn(
                // Base styles (Glassmorphism + Premium feel)
                "group relative overflow-hidden cursor-pointer",
                "bg-slate-900/30 backdrop-blur-sm border border-white/20",
                "rounded-xl sm:rounded-2xl shadow-2xl z-50", // z-50 to ensure it's above other elements
                "w-full md:w-96", // Mobile: Full width, Desktop: Fixed width
                "hover:bg-slate-900/40 transition-all duration-300",
                "hover:scale-[1.02] active:scale-[0.98]",
                "touch-manipulation", // Better touch handling on mobile
                className
            )}
        >
            <div className="flex flex-row md:flex-col h-64 sm:h-72 md:h-auto">
                {/* Structure Mobile: Row (Image Left, Content Right) */}
                {/* Structure Desktop: Column (Image Top, Content Bottom) via md:flex-col */}

                {/* Image Section */}
                <div className="w-48 sm:w-56 md:w-full md:h-56 relative shrink-0 bg-gradient-to-br from-slate-800/50 to-slate-900/50">
                    <AnimatePresence mode="sync">
                        {isImageLoading && (
                            <motion.div
                                key="image-loading"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="absolute inset-0 flex items-center justify-center z-10"
                            >
                                {/* AI Image Generation Animation with Gradient Bar */}
                                <div className="relative w-full h-full flex flex-col items-center justify-center gap-4 px-6">
                                    {/* Pulsing background */}
                                    <motion.div
                                        className="absolute inset-0 bg-gradient-to-br from-nomade-accent/10 via-nomade-primary/10 to-nomade-accent/10"
                                        animate={{
                                            opacity: [0.2, 0.4, 0.2],
                                        }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                        }}
                                    />
                                    {/* Gradient loading bar */}
                                    <div className="relative z-10 w-full max-w-xs">
                                        <GradientLoadingBar className="h-2" />
                                    </div>
                                    {/* Loading text */}
                                    <motion.p
                                        className="text-white/70 text-xs font-medium relative z-10"
                                        animate={{
                                            opacity: [0.5, 1, 0.5],
                                        }}
                                        transition={{
                                            duration: 1.5,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                        }}
                                    >
                                        Génération de l'image...
                                    </motion.p>
                                </div>
                            </motion.div>
                        )}
                        {displayImage && !imageError && (
                            <motion.img
                                key="image"
                                src={displayImage}
                                alt={circuit.name}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                loading="lazy"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: imageLoaded ? 1 : 0 }}
                                exit={{ opacity: 0 }}
                                onLoad={() => setImageLoaded(true)}
                                onError={() => setImageError(true)}
                            />
                        )}
                        {(!displayImage || imageError) && !isImageLoading && (
                            <motion.div
                                key="image-placeholder"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="absolute inset-0"
                            >
                                <ShimmerPlaceholder />
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60 pointer-events-none" />

                    {/* Favorite heart - orange */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(circuit);
                        }}
                        className="absolute top-3 left-3 z-10 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors pointer-events-auto"
                        aria-label={isFavorite(circuit.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                    >
                        <Heart
                            className={cn('w-5 h-5 text-nomade-accent', isFavorite(circuit.id) && 'fill-nomade-accent')}
                        />
                    </button>

                    {/* Badge (Desktop only) */}
                    <div className="hidden md:block absolute top-3 right-3 bg-nomade-red text-white text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wider shadow-lg">
                        Aperçu
                    </div>
                </div>

                {/* Content Section - fond quasi-opaque pour éviter artefacts de transparence pendant la rotation du carousel */}
                <div className="flex-1 p-3.5 sm:p-4 md:p-5 flex flex-col justify-start text-left min-w-0 overflow-y-auto">
                    <div className="bg-slate-900/85 rounded-xl flex flex-col justify-start text-left -m-3.5 sm:-m-4 md:-m-5 p-3.5 sm:p-4 md:p-5">
                        <h3 className="text-white font-bold text-sm sm:text-base md:text-xl leading-snug mb-2 md:mb-2 drop-shadow-md">
                            {circuit.name}
                        </h3>

                        <div className="flex items-center flex-wrap gap-2 sm:gap-3 text-white/90 text-xs md:text-sm mb-1.5 md:mb-3">
                        <div className="flex items-center">
                            <Clock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 text-nomade-accent shrink-0" />
                            <span className="whitespace-nowrap">{circuit.duration}j</span>
                        </div>
                        <div className="flex items-center">
                            <Euro className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 text-nomade-accent shrink-0" />
                            <span className="whitespace-nowrap">dès {circuit.price}€</span>
                        </div>
                        {circuit.niveau && (
                            <div className="flex items-center">
                                <Mountain className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 text-nomade-accent shrink-0" />
                                <span className="whitespace-nowrap">{circuit.niveau}</span>
                            </div>
                        )}
                    </div>

                    {/* Abstract - Show on mobile when in carousel */}
                    <div className="block text-white/70 text-xs sm:text-sm line-clamp-3 mb-2 md:mb-4 leading-relaxed min-h-[3rem]">
                        <AnimatePresence mode="sync">
                            {isTextLoading ? (
                                <motion.div
                                    key="text-loading"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="flex flex-col gap-2"
                                >
                                    {/* Gradient loading bar */}
                                    <GradientLoadingBar className="h-1.5" />
                                    {/* Loading text */}
                                    <motion.span
                                        className="text-white/50 text-xs"
                                        animate={{
                                            opacity: [0.5, 1, 0.5],
                                        }}
                                        transition={{
                                            duration: 1.5,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                        }}
                                    >
                                        Génération du résumé...
                                    </motion.span>
                                </motion.div>
                            ) : (
                                <motion.p
                                    key="text-content"
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -5 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {displayResume}
                                </motion.p>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* CTA line: Plus de détails + Ajouter favoris */}
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center text-nomade-accent text-xs sm:text-sm font-semibold group-hover:translate-x-1 transition-transform">
                            Plus de détails <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                        </div>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(circuit);
                            }}
                            className="flex items-center gap-1 text-nomade-accent text-xs sm:text-sm font-semibold shrink-0 hover:opacity-90 transition-opacity"
                            aria-label={isFavorite(circuit.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                        >
                            <Heart
                                className={cn('w-4 h-4 sm:w-4 sm:h-4 text-nomade-accent', isFavorite(circuit.id) && 'fill-nomade-accent')}
                            />
                            + ajouter favoris
                        </button>
                    </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
